import React, { useState, useEffect } from 'react';
import { auth, db, callFunction } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, getDoc } from 'firebase/firestore';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';
import { publishAttestation } from '../attestation';
import { isMotorPolicy, openMotorCertificatePrint, printPayloadFromPolicy } from '../pulsePrint';
import { ensureApproverKey, fingerprintSpki, signPolicyApproval } from '../signing';
import CryptoSignaturePanel from '../components/CryptoSignaturePanel';

function ManagerDashboard() {
    const { session, loading: sessionLoading } = useCompanySession();
    const [pendingPolicies, setPendingPolicies] = useState([]);
    const [approvedPolicies, setApprovedPolicies] = useState([]);
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [approvalNote, setApprovalNote] = useState('');
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [managerInfo, setManagerInfo] = useState(null);
    const [signatureLoading, setSignatureLoading] = useState(false);
    const [signatureError, setSignatureError] = useState('');
    const [willSign, setWillSign] = useState(false);
    const [keyFp, setKeyFp] = useState('');

    useEffect(() => {
        if (!session?.tenantId) return;
        loadManagerInfo(session.uid, session.tenantId);
        loadPendingPolicies(session.tenantId);
    }, [session]);

    useEffect(() => {
        if (!showApprovalModal || !session?.tenantId || !session?.uid) return;
        ensureApproverKey({ tenantId: session.tenantId, uid: session.uid })
            .then((pair) => fingerprintSpki(pair.publicKeySpki))
            .then(setKeyFp)
            .catch((err) => setSignatureError(err.message || 'Could not prepare your signing key.'));
    }, [showApprovalModal, session]);

    const loadManagerInfo = async (uid, tenantId) => {
        try {
            const managerDoc = await getDoc(doc(db, 'tenants', tenantId, 'users', uid));
            if (managerDoc.exists()) {
                setManagerInfo(managerDoc.data());
            } else {
                setManagerInfo({
                    displayName: auth.currentUser?.displayName || 'Manager',
                    email: auth.currentUser?.email || 'manager@company.com',
                    role: 'manager',
                    branch: 'Head Office'
                });
            }
        } catch (error) {
            console.error('Error loading manager info:', error);
            setManagerInfo({
                displayName: auth.currentUser?.displayName || 'Manager',
                email: auth.currentUser?.email || 'manager@company.com',
                role: 'manager',
                branch: 'Head Office'
            });
        }
    };

    const loadPendingPolicies = async (tenantId = session?.tenantId) => {
        if (!tenantId) return;
        try {
            const pendingQuery = query(
                collection(db, 'tenants', tenantId, 'policies'),
                where('status', '==', 'pending')
            );
            const pendingSnap = await getDocs(pendingQuery);
            
            const pendingData = [];
            pendingSnap.forEach(doc => {
                pendingData.push({ id: doc.id, ...doc.data() });
            });
            setPendingPolicies(pendingData);

            const approvedQuery = query(
                collection(db, 'tenants', tenantId, 'policies'),
                where('status', '==', 'ACTIVE')
            );
            const approvedSnap = await getDocs(approvedQuery);
            const approvedData = [];
            approvedSnap.forEach(doc => {
                approvedData.push({ id: doc.id, ...doc.data() });
            });
            const approvedLower = await getDocs(query(
                collection(db, 'tenants', tenantId, 'policies'),
                where('status', '==', 'active')
            ));
            approvedLower.forEach((d) => approvedData.push({ id: d.id, ...d.data() }));
            setApprovedPolicies(approvedData.slice(0, 10));
        } catch (error) {
            console.error('Error loading policies:', error);
        }
        setLoading(false);
    };

    const handleApprove = (policy) => {
        setSelectedPolicy(policy);
        setApprovalNote('');
        setSignatureError('');
        setWillSign(false);
        setShowApprovalModal(true);
    };

    const sendApprovalEmail = async (policy, signatureData) => {
        try {
            await callFunction('sendApprovalEmail', {
                policyId: policy.id,
                signatureId: signatureData.signatureId
            });
            return true;
        } catch (error) {
            console.error('Email error:', error);
            return false;
        }
    };

    const confirmApproval = async () => {
        if (!selectedPolicy) {
            setSignatureError('No policy selected');
            return;
        }
        if (!auth.currentUser || auth.currentUser.uid !== session.uid) {
            setSignatureError('The signed-in account does not match this session. Sign in again.');
            return;
        }
        if (!willSign) {
            setSignatureError('You must confirm that you are applying your personal cryptographic signature.');
            return;
        }

        setSignatureLoading(true);
        setSignatureError('');

        const tenantId = session.tenantId;
        try {
            const signer = {
                uid: auth.currentUser.uid,
                email: auth.currentUser.email,
                displayName: managerInfo?.displayName || session.displayName || auth.currentUser.email,
                role: session.role || managerInfo?.role || 'manager',
                tenantId
            };
            const signatureData = await signPolicyApproval({
                session: signer,
                policy: selectedPolicy,
                note: approvalNote
            });

            await updateDoc(
                doc(db, 'tenants', tenantId, 'policies', selectedPolicy.id),
                {
                    status: 'ACTIVE',
                    cardIssued: true,
                    approvedBy: signer.uid,
                    approvedByName: signer.displayName,
                    approvedByEmail: signer.email,
                    approvedAt: new Date(),
                    approvalNotes: approvalNote || 'Policy approved',
                    digitalSignature: signatureData,
                    signatureId: signatureData.signatureId,
                    approvedDate: signatureData.signedAt,
                    digitalStamp: {
                        managerName: signer.displayName,
                        managerEmail: signer.email,
                        managerUid: signer.uid,
                        position: signer.role,
                        branch: managerInfo?.branch || 'Head Office',
                        date: signatureData.signedAt,
                        signatureId: signatureData.signatureId,
                        signatureHash: signatureData.payloadHash,
                        alg: signatureData.alg,
                        fingerprint: signatureData.fingerprint
                    }
                }
            );

            await addDoc(collection(db, 'tenants', tenantId, 'approval_logs'), {
                policyId: selectedPolicy.id,
                policyNumber: selectedPolicy.policyNumber || 'PENDING',
                action: 'approved',
                managerId: signer.uid,
                managerName: signer.displayName,
                managerEmail: signer.email,
                signatureId: signatureData.signatureId,
                signatureHash: signatureData.payloadHash,
                alg: signatureData.alg,
                fingerprint: signatureData.fingerprint,
                notes: approvalNote || 'Policy approved',
                timestamp: new Date()
            });

            try {
                await publishAttestation({
                    tenantId,
                    companyName: session.companyName,
                    customer: selectedPolicy.policyHolder || { fullName: selectedPolicy.customerName || '' },
                    policy: { ...selectedPolicy, status: 'ACTIVE' }
                });
            } catch (err) {
                console.error(err);
            }

            await sendApprovalEmail(selectedPolicy, signatureData);

            // Refresh lists
            await loadPendingPolicies();
            setShowApprovalModal(false);
            setApprovalNote('');
            setSelectedPolicy(null);
            setSignatureLoading(false);

            alert('✅ Policy approved successfully!\n\n' +
                  '📧 Email notification sent.\n' +
                  '🔐 Signature ID: ' + signatureData.signatureId);

            if (isMotorPolicy(selectedPolicy)) {
                openMotorCertificatePrint({
                    certificates: [printPayloadFromPolicy({
                        ...selectedPolicy,
                        status: 'ACTIVE',
                        digitalSignature: signatureData,
                        approvedByName: signer.displayName
                    }, selectedPolicy.policyHolder, {
                        managerName: signer.displayName,
                        examinedBy: signer.displayName,
                        issuedAt: signatureData.signedAt,
                        signature: signatureData
                    })]
                });
            }
        } catch (error) {
            console.error('Error approving policy:', error);
            setSignatureError('Error approving policy: ' + error.message);
            setSignatureLoading(false);
        }
    };

    const handleReject = async (policy) => {
        const reason = prompt('Please enter reason for rejection:');
        if (!reason) return;

        const tenantId = session.tenantId;
        try {
            await updateDoc(
                doc(db, 'tenants', tenantId, 'policies', policy.id),
                {
                    status: 'rejected',
                    rejectionReason: reason,
                    rejectedAt: new Date(),
                    rejectedBy: auth.currentUser.uid,
                    rejectedByName: managerInfo?.displayName || 'Manager'
                }
            );
            
            // Log rejection
            await addDoc(collection(db, 'tenants', tenantId, 'approval_logs'), {
                policyId: policy.id,
                policyNumber: policy.policyNumber || 'PENDING',
                action: 'rejected',
                managerId: auth.currentUser.uid,
                managerName: managerInfo?.displayName || 'Manager',
                reason: reason,
                timestamp: new Date()
            });

            await loadPendingPolicies();
            alert('❌ Policy rejected');
        } catch (error) {
            console.error('Error rejecting policy:', error);
            alert('Error rejecting policy: ' + error.message);
        }
    };

    const renderDigitalSignature = (policy) => {
        return <CryptoSignaturePanel sig={policy.digitalSignature} />;
    };

    if (sessionLoading || !session || loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <CompanyLayout session={session}>
            <div>
                <div className="row mb-4">
                    <div className="col-md-4">
                        <div className="card text-white bg-warning">
                            <div className="card-body">
                                <h5>Pending Approvals</h5>
                                <h2>{pendingPolicies.length}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card text-white bg-success">
                            <div className="card-body">
                                <h5>Approved</h5>
                                <h2>{approvedPolicies.length}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card text-white bg-info">
                            <div className="card-body">
                                <h5>Digital Signatures</h5>
                                <h2>{approvedPolicies.filter(p => p.digitalSignature).length}</h2>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header" style={{ backgroundColor: '#ff6f00', color: 'white' }}>
                                <h5 className="mb-0">📋 Pending Policies for Approval</h5>
                            </div>
                            <div className="card-body">
                                {pendingPolicies.length === 0 ? (
                                    <div className="text-center text-muted py-4">
                                        <div className="display-1">✅</div>
                                        <h5>No pending policies</h5>
                                        <p>All policies have been reviewed</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Policy #</th>
                                                    <th>Client</th>
                                                    <th>Vehicle</th>
                                                    <th>Premium</th>
                                                    <th>Submitted</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pendingPolicies.map((policy) => (
                                                    <tr key={policy.id}>
                                                        <td>
                                                            <strong>{policy.policyNumber || 'PENDING'}</strong>
                                                            <br />
                                                            <small className="text-muted">{policy.coverageType}</small>
                                                        </td>
                                                        <td>{policy.policyHolder?.fullName || 'Unknown'}</td>
                                                        <td>{policy.vehicle?.make} {policy.vehicle?.model}</td>
                                                        <td>{policy.premiumAmount?.toLocaleString()} GMD</td>
                                                        <td>
                                                            {policy.submittedAt?.toDate?.()?.toLocaleDateString() || 'Today'}
                                                            <br />
                                                            <small className="text-muted">By: {policy.submittedBy}</small>
                                                        </td>
                                                        <td>
                                                            <button 
                                                                className="btn btn-success btn-sm me-1"
                                                                onClick={() => handleApprove(policy)}
                                                            >
                                                                ✅ Approve
                                                            </button>
                                                            <button 
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => handleReject(policy)}
                                                            >
                                                                ❌ Reject
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header" style={{ backgroundColor: '#2e7d32', color: 'white' }}>
                                <h5 className="mb-0">✅ Recently Approved with Digital Signatures</h5>
                            </div>
                            <div className="card-body">
                                {approvedPolicies.length === 0 ? (
                                    <div className="text-center text-muted py-4">No approved policies yet</div>
                                ) : (
                                    <div className="row">
                                        {approvedPolicies.map((policy) => (
                                            <div className="col-md-6 col-lg-4 mb-3" key={policy.id}>
                                                <div className="card border-success">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between">
                                                            <strong>{policy.policyNumber}</strong>
                                                            <span className="badge bg-success">Active</span>
                                                        </div>
                                                        <p className="mb-1">
                                                            <small>{policy.policyHolder?.fullName}</small>
                                                        </p>
                                                        <p className="mb-1">
                                                            <small className="text-muted">
                                                                {policy.vehicle?.make} {policy.vehicle?.model}
                                                            </small>
                                                        </p>
                                                        <hr />
                                                        {renderDigitalSignature(policy)}
                                                        {isMotorPolicy(policy) && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-success btn-sm mt-2"
                                                                onClick={() => openMotorCertificatePrint({
                                                                    certificates: [printPayloadFromPolicy(policy, policy.policyHolder)]
                                                                })}
                                                            >
                                                                Print certificate
                                                            </button>
                                                        )}
                                                        {policy.approvalNotes && (
                                                            <div className="mt-2">
                                                                <small className="text-muted">
                                                                    📝 Notes: {policy.approvalNotes}
                                                                </small>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Approval Modal */}
            {showApprovalModal && selectedPolicy && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}>
                    <div className="modal-dialog modal-lg" style={{ marginTop: '50px' }}>
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: '#1a237e', color: 'white' }}>
                                <h5 className="modal-title">🔐 Digital Signature Approval</h5>
                                <button className="btn-close btn-close-white" onClick={() => setShowApprovalModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {signatureError && (
                                    <div className="alert alert-danger">{signatureError}</div>
                                )}
                                
                                <div className="row">
                                    <div className="col-md-6">
                                        <h6>Policy Details</h6>
                                        <p><strong>Policy:</strong> {selectedPolicy.policyNumber || 'PENDING'}</p>
                                        <p><strong>Client:</strong> {selectedPolicy.policyHolder?.fullName}</p>
                                        <p><strong>Vehicle:</strong> {selectedPolicy.vehicle?.make} {selectedPolicy.vehicle?.model}</p>
                                        <p><strong>Premium:</strong> {selectedPolicy.premiumAmount?.toLocaleString()} GMD</p>
                                        <p><strong>Coverage:</strong> {selectedPolicy.coverageType}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <h6>Your cryptographic signature</h6>
                                        <div className="border p-3" style={{ borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                                            <div style={{ fontFamily: 'cursive', fontSize: '22px', color: '#1a237e' }}>
                                                {managerInfo?.displayName || session.displayName || auth.currentUser?.email}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#334155' }}>
                                                {auth.currentUser?.email}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                {session.role || managerInfo?.role} · UID {session.uid.slice(0, 8)}…
                                            </div>
                                            <hr />
                                            <div style={{ fontSize: '11px', color: '#334155' }}>
                                                <strong>ECDSA P-256</strong> key on this device. The private key never leaves the browser.
                                            </div>
                                            <div className="font-monospace mt-2" style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                                                {keyFp || 'Preparing signing key…'}
                                            </div>
                                        </div>
                                        <label className="form-check mt-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={willSign}
                                                onChange={(e) => setWillSign(e.target.checked)}
                                            />
                                            <span className="form-check-label">
                                                I am {auth.currentUser?.email} and I apply my personal cryptographic signature to this policy.
                                            </span>
                                        </label>
                                    </div>
                                </div>
                                
                                <div className="mt-3">
                                    <label className="form-label">Approval Notes (Optional)</label>
                                    <textarea 
                                        className="form-control" 
                                        rows="2"
                                        value={approvalNote}
                                        onChange={(e) => setApprovalNote(e.target.value)}
                                        placeholder="Add any notes about this approval..."
                                        disabled={signatureLoading}
                                    ></textarea>
                                </div>
                                
                                <div className="mt-3 alert alert-info">
                                    <small>
                                        Approval is refused unless this signed-in user creates a valid ECDSA P-256 signature over the policy. The private key stays on this device; the public key and signature are stored on the policy for later verification.
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowApprovalModal(false)}
                                    disabled={signatureLoading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-success" 
                                    onClick={confirmApproval}
                                    disabled={signatureLoading || !willSign || !keyFp}
                                >
                                    {signatureLoading ? 'Signing…' : 'Sign and approve'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </CompanyLayout>
    );
}

export default ManagerDashboard;
